import { Dropdown } from "@/components/Dropdown";

type PagerProps = {
  total: number;
  limit: number;
  offset: number;
  onOffsetChange: (next: number) => void;
  onLimitChange: (next: number) => void;
  pageSizes?: number[];
};

/**
 * "Showing 1–30 of 52" + numeric page buttons + per-page dropdown.
 * Sits inside `<div class="table-footer">` — caller wraps.
 *
 * We emit (offset, limit) rather than (page) so the hook that wraps
 * /api/stats/* queries can pass them straight through as query params.
 */
export function Pager({
  total,
  limit,
  offset,
  onOffsetChange,
  onLimitChange,
  pageSizes = [30, 50, 75, 100, 150],
}: PagerProps) {
  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));
  const first = total === 0 ? 0 : offset + 1;
  const last = Math.min(offset + limit, total);

  // Build a compact list of page numbers with ellipses. Always show
  // first + last + current±1.
  const pages = buildPageList(page, pageCount);

  return (
    <div className="table-footer">
      <div style={{ fontSize: 13, color: "var(--c-text-3)" }}>
        Showing <b style={{ color: "var(--c-text)" }}>{first}–{last}</b> of{" "}
        <b style={{ color: "var(--c-text)" }}>{total.toLocaleString()}</b>
      </div>

      <div className="pager">
        <button
          className="pg-btn"
          disabled={page === 1}
          onClick={() => onOffsetChange(Math.max(0, offset - limit))}
        >
          ‹
        </button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e${i}`} className="pg-ellipsis">
              …
            </span>
          ) : (
            <button
              key={p}
              className={"pg-btn" + (p === page ? " active" : "")}
              onClick={() => onOffsetChange((p - 1) * limit)}
            >
              {p}
            </button>
          )
        )}
        <button
          className="pg-btn"
          disabled={page === pageCount}
          onClick={() => onOffsetChange(Math.min((pageCount - 1) * limit, offset + limit))}
        >
          ›
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ fontSize: 12.5, color: "var(--c-text-3)" }}>Per page</span>
        <Dropdown<string>
          value={String(limit)}
          onChange={(v) => {
            onLimitChange(Number(v));
            onOffsetChange(0);
          }}
          options={pageSizes.map((n) => ({ value: String(n), label: String(n) }))}
          align="right"
          direction="up"
        />
      </div>
    </div>
  );
}

/** `buildPageList(5, 20)` → `[1, "…", 4, 5, 6, "…", 20]`. */
function buildPageList(page: number, pageCount: number): (number | "…")[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out: (number | "…")[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  if (start > 2) out.push("…");
  for (let p = start; p <= end; p++) out.push(p);
  if (end < pageCount - 1) out.push("…");
  out.push(pageCount);
  return out;
}
